import type {CalypsoCatalog} from "#pricing/catalogs/calypso/catalog"
import type {KeyedVariants} from "#pricing/catalogs/graph-helpers"
import {
    createAmountTriggeredPriceNode,
    createKeyedVariantPriceNodes,
    createPriceContainer,
    createVariantConditionedPriceNodes,
} from "#pricing/catalogs/graph-helpers"
import type {ConfigurationGroupNode} from "#pricing/nodes/configuration-group-node"
import {MeshAmountNode} from "#pricing/nodes/mesh-amount-node"
import type {MeshesNode} from "#pricing/nodes/meshes-node"
import type {PriceContainer} from "#pricing/nodes/price-container"

/** The Lori taps only come in chrome, so mesh presence alone prices them. */
export function createLoriTapsContainer(catalog: CalypsoCatalog, Meshes: MeshesNode): PriceContainer {
    const loriAmount = new MeshAmountNode({parameters: {name: "1112CHR - Lori M/Block Basin Tap", meshes: Meshes}})
    const lori = createAmountTriggeredPriceNode(catalog, "1112CHR", loriAmount)

    const loriTallAmount = new MeshAmountNode({parameters: {name: "1113CHR - Lori Tall M/Block Basin Tap", meshes: Meshes}})
    const loriTall = createAmountTriggeredPriceNode(catalog, "1113CHR", loriTallAmount)

    return createPriceContainer("Lori Taps", "Lori Taps", [lori, loriTall], true)
}

/** The Melfi taps only come in chrome, so mesh presence alone prices them. */
export function createMelfiTapsContainer(catalog: CalypsoCatalog, Meshes: MeshesNode): PriceContainer {
    const melfiMonoBlockAmount = new MeshAmountNode({parameters: {name: "1101 - Melfi MBlock Basin Tap", meshes: Meshes}})
    const melfiMonoBlock = createAmountTriggeredPriceNode(catalog, "1101", melfiMonoBlockAmount)

    const melfiTallMonoAmount = new MeshAmountNode({parameters: {name: "1102 - Melfi Tall Mono Basin Tap", meshes: Meshes}})
    const melfiTallMono = createAmountTriggeredPriceNode(catalog, "1102", melfiTallMonoAmount)

    return createPriceContainer("Melfi Taps", "Melfi Taps", [melfiMonoBlock, melfiTallMono], true)
}

export type ImolaTapMeshNames = {monoBlock: string; threeHole: string; wallMount: string}

/** Imola tap mesh names of the Tesima range templates. */
export const tesimaImolaTapMeshNames: ImolaTapMeshNames = {
    monoBlock: "Imola M/Block Basin Tap",
    threeHole: "Imola 3 Hole Basin Tap",
    wallMount: "Imola Wall Mount Basin Tap",
}

/**
 * Each Imola tap is triggered by its mesh, the finish by the accessory finish variant. Both must hold for a node to price.
 * The catalog lists these taps in chrome and matt black only, both of which the accessory finish map resolves by description.
 * The mesh names are passed in because the Imola taps live in different templates with different mesh names (see the callers).
 */
export function createImolaTapsContainer(
    catalog: CalypsoCatalog,
    Meshes: MeshesNode,
    accessoryFinishConfigMap: Map<string, [ConfigurationGroupNode, string]>,
    meshNames: ImolaTapMeshNames = tesimaImolaTapMeshNames,
): PriceContainer {
    const imolaMonoBlockAmount = new MeshAmountNode({parameters: {name: meshNames.monoBlock, meshes: Meshes}})
    const imolaMonoBlockUnits = createVariantConditionedPriceNodes(catalog, "1111", 2, accessoryFinishConfigMap, imolaMonoBlockAmount)

    const imola3HoleAmount = new MeshAmountNode({parameters: {name: meshNames.threeHole, meshes: Meshes}})
    const imola3HoleUnits = createVariantConditionedPriceNodes(catalog, "1166", 2, accessoryFinishConfigMap, imola3HoleAmount)

    const imolaWallMountAmount = new MeshAmountNode({parameters: {name: meshNames.wallMount, meshes: Meshes}})
    const imolaWallMountUnits = createVariantConditionedPriceNodes(catalog, "1167", 2, accessoryFinishConfigMap, imolaWallMountAmount)

    return createPriceContainer("Imola Taps", "Imola Taps", [...imolaMonoBlockUnits, ...imola3HoleUnits, ...imolaWallMountUnits], true)
}

/** Triggered by its mesh, the finish by the accessory finish variant. The catalog lists it in chrome, brushed brass and matt black. */
export function createFieraTapsContainer(
    catalog: CalypsoCatalog,
    Meshes: MeshesNode,
    accessoryFinishConfigMap: Map<string, [ConfigurationGroupNode, string]>,
): PriceContainer {
    const fieraMonoAmount = new MeshAmountNode({parameters: {name: "1141_-_Fiera_Mono_Basin_Tap_c-w_Slotted_Waste", meshes: Meshes}})
    const fieraMonoUnits = createVariantConditionedPriceNodes(catalog, "1141", 3, accessoryFinishConfigMap, fieraMonoAmount)

    return createPriceContainer("Fiera Taps", "Fiera Taps", fieraMonoUnits, true)
}

/** Triggered by its mesh, the finish by the accessory finish variant. The catalog lists it in chrome, brushed brass and matt black. */
export function createEulaTapsContainer(
    catalog: CalypsoCatalog,
    Meshes: MeshesNode,
    accessoryFinishConfigMap: Map<string, [ConfigurationGroupNode, string]>,
): PriceContainer {
    const eulaMonoBlockAmount = new MeshAmountNode({parameters: {name: "1126 - Eula M-Block Basin Tap c-w Slot'd Click Waste", meshes: Meshes}})
    const eulaMonoBlockUnits = createVariantConditionedPriceNodes(catalog, "1126", 3, accessoryFinishConfigMap, eulaMonoBlockAmount)

    return createPriceContainer("Eula Taps", "Eula Taps", eulaMonoBlockUnits, true)
}

/**
 * Each Busca tap is triggered by its mesh, the finish by the accessory finish variant. The catalog lists six finishes per tap, one
 * row per SKU suffix (CHR, MBK, BBR, NBB, GML, BBZ).
 *
 * The finish is resolved by SKU suffix, not by description: "Natural Brushed Brass" contains "Brushed Brass", so neither a
 * description-keyed config map nor a description-based exclude list can tell the two rows apart. `finishSuffixMap` holds one entry
 * per finish the template offers, mapping the SKU suffix to the accessory finish variant that selects it; every lookup expects
 * exactly one catalog row. Templates with one finish group per handle design pass a funnel (SKU suffix keys) instead.
 */
export function createBuscaTapsContainer(
    catalog: CalypsoCatalog,
    Meshes: MeshesNode,
    finishSuffixMap: Map<string, [ConfigurationGroupNode, string]> | KeyedVariants,
): PriceContainer {
    const createBuscaTapNodes = (skuStem: string, meshName: string) => {
        const amount = new MeshAmountNode({parameters: {name: meshName, meshes: Meshes}})
        if (!(finishSuffixMap instanceof Map))
            return [...finishSuffixMap.variantIdByKey.keys()].flatMap((suffix) =>
                createKeyedVariantPriceNodes(catalog, skuStem + suffix, 1, finishSuffixMap, {amount}),
            )
        return [...finishSuffixMap].flatMap(([suffix, [configGroup, variantId]]) => {
            const nodes = createVariantConditionedPriceNodes(catalog, skuStem + suffix, 1, undefined, amount)
            nodes.forEach((node) => node.addDependency(configGroup, variantId, ""))
            return nodes
        })
    }

    const buscaMonoBlockUnits = createBuscaTapNodes("1123", "1123 - busca_mblock_basin_tap_main")
    const buscaWallMountUnits = createBuscaTapNodes("1021", "1021 - busca_wall_mounted_basin_tap")
    const buscaTallMonoUnits = createBuscaTapNodes("1133", "1133 - busca_tall_mono_basin_tap_main")

    return createPriceContainer("Busca Taps", "Busca Taps", [...buscaMonoBlockUnits, ...buscaWallMountUnits, ...buscaTallMonoUnits], true)
}

export type AquilaTapMeshNames = {mono: string; wallMount: string; tall: string}

/** Aquila tap mesh names of the Tesima range templates. */
export const tesimaAquilaTapMeshNames: AquilaTapMeshNames = {
    mono: "1144 - Aquila Mono Basin Tap",
    wallMount: "1164 - Aquila_Wall_Mounted_Basin_Tap",
    tall: "1159_-_Aquila_Tall_Basin_Tap",
}

/**
 * Each Aquila tap is triggered by its mesh, the finish by the accessory finish variant. The catalog lists all three in
 * chrome, brushed brass and matt black. Templates with one finish group per handle design pass a funnel (description keys) instead of
 * the config map; the mesh names are passed in because the Aquila taps live in templates with different mesh names.
 */
export function createAquilaTapsContainer(
    catalog: CalypsoCatalog,
    Meshes: MeshesNode,
    accessoryFinish: Map<string, [ConfigurationGroupNode, string]> | KeyedVariants,
    meshNames: AquilaTapMeshNames = tesimaAquilaTapMeshNames,
): PriceContainer {
    const createAquilaTapNodes = (sku: string, amount: MeshAmountNode) =>
        accessoryFinish instanceof Map
            ? createVariantConditionedPriceNodes(catalog, sku, 3, accessoryFinish, amount)
            : createKeyedVariantPriceNodes(catalog, sku, 3, accessoryFinish, {amount})

    const aquilaMonoAmount = new MeshAmountNode({parameters: {name: meshNames.mono, meshes: Meshes}})
    const aquilaMonoUnits = createAquilaTapNodes("1144", aquilaMonoAmount)

    const aquilaWallMountAmount = new MeshAmountNode({parameters: {name: meshNames.wallMount, meshes: Meshes}})
    const aquilaWallMountUnits = createAquilaTapNodes("1164", aquilaWallMountAmount)

    const aquilaTallAmount = new MeshAmountNode({parameters: {name: meshNames.tall, meshes: Meshes}})
    const aquilaTallUnits = createAquilaTapNodes("1159", aquilaTallAmount)

    return createPriceContainer("Aquila Taps", "Aquila Taps", [...aquilaMonoUnits, ...aquilaWallMountUnits, ...aquilaTallUnits], true)
}

/** Triggered by its mesh, the finish by the accessory finish variant. The catalog lists it in chrome, brushed brass and matt black. */
export function createLonatoTapsContainer(
    catalog: CalypsoCatalog,
    Meshes: MeshesNode,
    accessoryFinishConfigMap: Map<string, [ConfigurationGroupNode, string]>,
): PriceContainer {
    const lonatoMonoAmount = new MeshAmountNode({parameters: {name: "1136 - Lonato", meshes: Meshes}})
    const lonatoMonoUnits = createVariantConditionedPriceNodes(catalog, "1136", 3, accessoryFinishConfigMap, lonatoMonoAmount)

    return createPriceContainer("Lonato Taps", "Lonato Taps", lonatoMonoUnits, true)
}
